import { useState, useRef, useEffect } from "react";
import {
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Input,
  DropdownSection,
  Checkbox,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from "@heroui/react";
import { PlusIcon } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { useMediaQuery } from "@mantine/hooks";

type Label = {
  id: number;
  name: string;
};

function toggleLabel(selectedLabelIds: number[], labelId: number): number[] {
  return selectedLabelIds.includes(labelId)
    ? selectedLabelIds.filter((id) => id !== labelId)
    : [...selectedLabelIds, labelId];
}

function LimeLabelSelectorContent({
  allLabels,
  selectedLabelIds,
  onLabelsChange,
  onCreateLabel,
  searchQuery,
  setSearchQuery,
  inputRef,
  onClose,
}: {
  allLabels: Label[];
  selectedLabelIds: number[];
  onLabelsChange: (labelIds: number[]) => void;
  onCreateLabel: (name: string) => Promise<number>;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onClose: () => void;
}) {
  const selectedLabels = allLabels.filter((label) =>
    selectedLabelIds.includes(label.id),
  );
  const unselectedLabels = allLabels.filter(
    (label) => !selectedLabelIds.includes(label.id),
  );
  const filteredUnselectedLabels = unselectedLabels.filter((label) =>
    label.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleLabelToggle = (labelId: number) => {
    onLabelsChange(toggleLabel(selectedLabelIds, labelId));
    if (onClose) onClose(); // On mobile, close after selection
  };

  return (
    <div className="flex flex-col gap-2 p-2">
      <Input
        ref={inputRef}
        placeholder={t`Vnesite ime oznake...`}
        className="m-0 p-0"
        size="sm"
        radius="sm"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClick={(e) => e.preventDefault()}
      />
      {selectedLabels.length > 0 && (
        <div className="mt-2 pb-2">
          {selectedLabels.map((label) => (
            <div key={label.id} className="flex items-center py-1">
              <Checkbox
                isSelected={selectedLabelIds.includes(label.id)}
                onValueChange={() => handleLabelToggle(label.id)}
                size="sm"
                radius="sm"
              >
                {label.name}
              </Checkbox>
            </div>
          ))}
        </div>
      )}
      {filteredUnselectedLabels.length > 0 && (
        <div className="mt-2">
          {filteredUnselectedLabels.map((label) => (
            <div key={label.id} className="flex items-center py-1">
              <Checkbox
                isSelected={selectedLabelIds.includes(label.id)}
                onValueChange={() => handleLabelToggle(label.id)}
                size="sm"
                radius="sm"
              >
                {label.name}
              </Checkbox>
            </div>
          ))}
        </div>
      )}
      {searchQuery && filteredUnselectedLabels.length === 0 && (
        <Button
          startContent={<PlusIcon className="h-4 w-4" />}
          onPress={async () => {
            if (onCreateLabel && searchQuery) {
              const newLabelId = await onCreateLabel(searchQuery);
              onLabelsChange([...selectedLabelIds, newLabelId]);
              setSearchQuery("");
              onClose();
            }
          }}
          className="mt-2"
          size="sm"
        >
          <Trans>Ustvari novo oznako</Trans>: &quot;{searchQuery}&quot;
        </Button>
      )}
    </div>
  );
}

export const LimeLabelSelector = ({
  allLabels,
  selectedLabelIds,
  onLabelsChange,
  onCreateLabel,
  isDisabled,
}: {
  allLabels: Label[];
  selectedLabelIds: number[];
  onLabelsChange: (labelIds: number[]) => void;
  onCreateLabel: (name: string) => Promise<number>;
  isDisabled?: boolean;
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  if (isMobile) {
    return (
      <>
        <Button
          isIconOnly
          size="sm"
          radius="sm"
          className="h-8"
          variant="faded"
          onPress={isOpen ? onClose : onOpen}
          isDisabled={isDisabled}
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
        <Modal
          isOpen={isOpen}
          isDismissable={false}
          onOpenChange={(open) => {
            if (open) {
              onOpen();
            } else {
              onClose();
            }

            if (!open) setSearchQuery("");
          }}
        >
          <ModalContent className="h-[80vh]">
            <ModalHeader>
              <span className="text-lg font-semibold">{t`Izberi oznake`}</span>
            </ModalHeader>
            <ModalBody>
              <LimeLabelSelectorContent
                allLabels={allLabels}
                selectedLabelIds={selectedLabelIds}
                onLabelsChange={onLabelsChange}
                onCreateLabel={onCreateLabel}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                inputRef={inputRef}
                onClose={() => {}} // don't auto close on mobile
              />
            </ModalBody>
            <ModalFooter className="flex justify-end gap-2">
              <Button variant="light" onPress={onClose}>
                Prekliči
              </Button>
              <Button onPress={onClose} color="primary">
                OK
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </>
    );
  }

  // Desktop: dropdown
  return (
    <Dropdown
      placement="right-start"
      radius="sm"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (open) {
          onOpen();
        } else {
          onClose();
        }

        if (!open) {
          setSearchQuery("");
        }
      }}
      shouldCloseOnInteractOutside={(e) => {
        return !(e as HTMLElement).closest('[role="dialog"]');
      }}
      shouldCloseOnBlur={false}
      shouldCloseOnScroll={false}
    >
      <DropdownTrigger>
        <Button
          isIconOnly
          size="sm"
          radius="sm"
          className="h-8"
          variant="faded"
          isDisabled={isDisabled}
        >
          <PlusIcon className="h-4 w-4" />
        </Button>
      </DropdownTrigger>
      <DropdownMenu classNames={{}} closeOnSelect={false} variant="faded">
        <DropdownSection>
          <DropdownItem key="search" closeOnSelect={false} className="p-0">
            <Input
              ref={inputRef}
              placeholder={t`Vnesite ime oznake...`}
              className="m-0 p-0"
              size="sm"
              radius="sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.preventDefault()}
            />
          </DropdownItem>
        </DropdownSection>

        <DropdownSection
          hidden={
            allLabels.filter((label) => selectedLabelIds.includes(label.id))
              .length === 0
          }
          showDivider={true}
        >
          {allLabels
            .filter((label) => selectedLabelIds.includes(label.id))
            .map((label) => (
              <DropdownItem
                key={label.id}
                onPress={() => {
                  onLabelsChange(toggleLabel(selectedLabelIds, label.id));
                }}
                closeOnSelect={false}
              >
                <Checkbox
                  isSelected={selectedLabelIds.includes(label.id)}
                  size="sm"
                  radius="sm"
                >
                  {label.name}
                </Checkbox>
              </DropdownItem>
            ))}
        </DropdownSection>

        <DropdownSection
          hidden={
            allLabels.filter(
              (label) =>
                !selectedLabelIds.includes(label.id) &&
                label.name.toLowerCase().includes(searchQuery.toLowerCase()),
            ).length === 0
          }
        >
          {allLabels
            .filter(
              (label) =>
                !selectedLabelIds.includes(label.id) &&
                label.name.toLowerCase().includes(searchQuery.toLowerCase()),
            )
            .map((label) => (
              <DropdownItem
                key={label.id}
                onPress={() => {
                  onLabelsChange(toggleLabel(selectedLabelIds, label.id));
                }}
                closeOnSelect={true}
              >
                <Checkbox
                  isSelected={selectedLabelIds.includes(label.id)}
                  size="sm"
                  radius="sm"
                >
                  {label.name}
                </Checkbox>
              </DropdownItem>
            ))}
        </DropdownSection>

        <DropdownSection
          hidden={
            !searchQuery ||
            allLabels.filter(
              (label) =>
                !selectedLabelIds.includes(label.id) &&
                label.name.toLowerCase().includes(searchQuery.toLowerCase()),
            ).length > 0
          }
        >
          <DropdownItem
            key="create-new"
            startContent={<PlusIcon className="h-4 w-4" />}
            onPress={async () => {
              if (onCreateLabel && searchQuery) {
                const newLabelId = await onCreateLabel(searchQuery);

                onLabelsChange([...selectedLabelIds, newLabelId]);
                setSearchQuery("");

                onClose();
              }
            }}
          >
            <Trans>Ustvari novo oznako</Trans>: &quot;{searchQuery}&quot;
          </DropdownItem>
        </DropdownSection>
      </DropdownMenu>
    </Dropdown>
  );
};
